import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useConfirmation } from "../context/ConfirmationContext";
import API_URL from "../config";

const PERFIS = ["admin", "financeiro", "comum"];

const perfil_badge = {
  admin: "bg-purple-50 text-purple-700 border-purple-200",
  financeiro: "bg-blue-50 text-blue-700 border-blue-200",
  comum: "bg-gray-50 text-gray-600 border-gray-200",
};

export default function Usuarios() {
  const { token, usuario } = useAuth();
  const { confirm } = useConfirmation();
  const podeExcluir = usuario?.perfil === "admin" || usuario?.perfil === "financeiro";
  const [usuarios, setUsuarios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [modalCriar, setModalCriar] = useState(false);
  const [modalEditar, setModalEditar] = useState(null); // usuário sendo editado
  const [form, setForm] = useState({ nome: "", email: "", perfil: "comum" });
  const [formEditar, setFormEditar] = useState({ nome: "", perfil: "comum" });
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState({ tipo: "", texto: "" });
  const [modalSenha, setModalSenha] = useState(null); // { nome, email, senha, emailEnviado }

  const mostrarMsg = (tipo, texto) => {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg({ tipo: "", texto: "" }), 4000);
  };

  const carregarUsuarios = async () => {
    setCarregando(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/usuarios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const json = await res.json();
      setUsuarios(json);
    } catch {
      mostrarMsg("erro", "Erro ao carregar usuários.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregarUsuarios(); }, []);

  const handleCriar = async () => {
    if (!form.nome || !form.email || !form.perfil) return mostrarMsg("erro", "Preencha todos os campos.");
    setSalvando(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/usuarios`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json === "string" ? json : json.mensagem);
      setModalCriar(false);
      setForm({ nome: "", email: "", perfil: "comum" });
      carregarUsuarios();
      setModalSenha({ nome: form.nome, email: form.email, senha: json.senha, emailEnviado: json.emailEnviado });
    } catch (err) {
      mostrarMsg("erro", err.message);
    } finally {
      setSalvando(false);
    }
  };

  const handleEditar = async () => {
    if (!formEditar.nome || !formEditar.perfil) return mostrarMsg("erro", "Preencha todos os campos.");
    setSalvando(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/usuarios/${modalEditar.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formEditar),
      });
      const texto = await res.json();
      if (!res.ok) throw new Error(texto);
      mostrarMsg("ok", texto);
      setModalEditar(null);
      carregarUsuarios();
    } catch (err) {
      mostrarMsg("erro", err.message);
    } finally {
      setSalvando(false);
    }
  };

  const handleDeletar = async (id, nome) => {
    const confirmed = await confirm(
      `Deseja remover o usuário?`,
      `${nome} será removido permanentemente.`,
      "Remover"
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/usuarios/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const texto = await res.json();
      if (!res.ok) throw new Error(texto);
      mostrarMsg("ok", texto);
      carregarUsuarios();
    } catch (err) {
      mostrarMsg("erro", err.message);
    }
  };

  const handleResetarSenha = async (id, nome) => {
    const confirmed = await confirm(
      `Resetar senha de ${nome}?`,
      "Uma nova senha será enviada por e-mail.",
      "Resetar"
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_URL}/api/auth/usuarios/${id}/resetar-senha`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json === "string" ? json : json.mensagem);
      const u = usuarios.find((u) => u.id === id);
      setModalSenha({ nome, email: u?.email ?? "", senha: json.senha, emailEnviado: json.emailEnviado });
    } catch (err) {
      mostrarMsg("erro", err.message);
    }
  };

  const abrirEditar = (u) => {
    setFormEditar({ nome: u.nome, perfil: u.perfil });
    setModalEditar(u);
  };

  const formatarData = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col gap-6 py-6 px-4">

      {/* Header */}
      <div className="border-b-2 border-blue-600 pb-3 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-1">SINDIÁGUA-PB</p>
          <h1 className="text-2xl font-bold text-gray-800">Usuários</h1>
        </div>
        <button onClick={() => setModalCriar(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
          + Novo Usuário
        </button>
      </div>

      {/* Mensagem */}
      {msg.texto && (
        <div className={`px-4 py-3 rounded-lg text-sm font-medium border ${msg.tipo === "ok" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200"}`}>
          {msg.tipo === "ok" ? "✓" : "⚠"} {msg.texto}
        </div>
      )}

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-700">Usuários cadastrados</p>
          <span className="text-xs bg-blue-50 text-blue-600 font-semibold px-2 py-1 rounded-full">
            {usuarios.length} usuário{usuarios.length !== 1 ? "s" : ""}
          </span>
        </div>

        {carregando ? (
          <div className="px-6 py-10 text-center text-sm text-gray-400">Carregando...</div>
        ) : usuarios.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-400">Nenhum usuário cadastrado.</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {usuarios.map((u) => (
              <div key={u.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                    {u.nome.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{u.nome}</p>
                    <p className="text-xs text-gray-400">{u.email} &middot; cadastrado em {formatarData(u.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${perfil_badge[u.perfil]}`}>
                    {u.perfil}
                  </span>
                  <button onClick={() => abrirEditar(u)}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors">
                    ✏ Editar
                  </button>
                  <button onClick={() => handleResetarSenha(u.id, u.nome)}
                    className="px-3 py-1.5 text-xs font-semibold text-yellow-600 hover:bg-yellow-50 border border-yellow-200 rounded-lg transition-colors">
                    🔑 Resetar
                  </button>
                  {podeExcluir && usuario?.id !== u.id && (
                  <button onClick={() => handleDeletar(u.id, u.nome)}
                    className="px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50 border border-red-200 rounded-lg transition-colors">
                    🗑 Excluir
                  </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Criar */}
      {modalCriar && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Novo Usuário</h2>
              <button onClick={() => setModalCriar(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nome</label>
                <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">E-mail</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Perfil</label>
                <select value={form.perfil} onChange={(e) => setForm({ ...form, perfil: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition bg-white">
                  {PERFIS.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>
              <p className="text-xs text-gray-400">Uma senha provisória será gerada e enviada para o e-mail cadastrado.</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setModalCriar(false)}
                className="flex-1 h-10 border border-gray-200 text-gray-500 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleCriar} disabled={salvando}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white text-sm font-semibold rounded-lg transition-colors">
                {salvando ? "Salvando..." : "Criar Usuário"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Senha Gerada */}
      {modalSenha && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Senha Gerada</h2>
              <button onClick={() => setModalSenha(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {modalSenha.emailEnviado ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                ✓ Senha enviada para <strong>{modalSenha.email}</strong>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                ⚠ Falha ao enviar e-mail. Comunique a senha manualmente.
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Usuário</p>
              <p className="text-sm text-gray-700">{modalSenha.nome} &middot; {modalSenha.email}</p>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Senha provisória</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-lg font-mono font-bold tracking-widest text-gray-800 select-all">
                  {modalSenha.senha}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(modalSenha.senha)}
                  className="px-3 py-3 text-xs font-semibold text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                  title="Copiar senha">
                  📋
                </button>
              </div>
            </div>

            <p className="text-xs text-gray-400">Esta senha não será exibida novamente. Anote-a antes de fechar.</p>

            <button onClick={() => setModalSenha(null)}
              className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
              Entendido
            </button>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {modalEditar && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Editar Usuário</h2>
              <button onClick={() => setModalEditar(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Nome</label>
                <input type="text" value={formEditar.nome} onChange={(e) => setFormEditar({ ...formEditar, nome: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">E-mail</label>
                <input type="text" value={modalEditar.email} disabled
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-100 text-sm bg-gray-50 text-gray-400 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Perfil</label>
                <select value={formEditar.perfil} onChange={(e) => setFormEditar({ ...formEditar, perfil: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition bg-white">
                  {PERFIS.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setModalEditar(null)}
                className="flex-1 h-10 border border-gray-200 text-gray-500 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleEditar} disabled={salvando}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white text-sm font-semibold rounded-lg transition-colors">
                {salvando ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
